import React, { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toMultiDropdownOptions } from 'shared/utils/formUtils';
import Api from '../lib/api';
import { withLocalePrefix } from 'shared/utils/translation';
import { TertiaryButton } from '@grnhse/seedling/lib/birch/components/buttons';
import FieldObject from '../components/custom_fields/field_object';
import CustomField from '../components/custom_fields/custom_field';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack';
import { mapWithValueType, filterByTerm } from '../lib/utils';
import { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import { ButtonWrapper } from './index';

const t = withLocalePrefix('talent_search.form.sections.candidate.labels');

type Props = {
  apiClient: Api;
  currencies: Item[];
};

const Application = ({ apiClient, currencies }: Props) => {
  const { watch, setValue } = useFormContext();
  const userDateFormat = watch('user_date_format');
  const applicationFields: FieldObject[] = watch('application_fields');
  const queryClient = useQueryClient();

  const onAddField = () => {
    setValue('application_fields', [
      ...applicationFields,
      FieldObject.emptyField(userDateFormat),
    ]);
  };

  const onRemoveField = (index: number) => {
    applicationFields.splice(index, 1);
    setValue('application_fields', applicationFields, { shouldDirty: true });
  };

  const onCustomFieldChange = (item: Item | null, index: number) => {
    const fieldObject = applicationFields[index];
    fieldObject.setCustomField(item);
    applicationFields.splice(index, 1, fieldObject);
    setValue('application_fields', applicationFields, { shouldDirty: true });
  };

  const fetchApplicationFields = useCallback(
    async (term: string) => {
      const queryKey = ['applicationFields', term];

      const cachedData: Item[] | null | undefined =
        queryClient.getQueryData(queryKey);

      if (cachedData) {
        return { items: cachedData };
      }

      const items = await queryClient.fetchQuery({
        queryKey,
        queryFn: async () => {
          const fetchedFields = await apiClient.fetchApplicationCustomFields(
            term
          );
          const fieldsWithType = mapWithValueType(fetchedFields);
          const fieldItems = fieldsWithType.length
            ? fieldsWithType[0].children
            : [];
          return toMultiDropdownOptions(fieldItems);
        },
      });

      return { items };
    },
    [apiClient, queryClient]
  );

  // for searching options of the custom field selected
  const fetchCustomFieldOptions = useCallback(
    async (id: string, term: string) => {
      const queryKey = ['applicationFieldOptions', id];

      const cachedData: Item[] | null | undefined =
        queryClient.getQueryData(queryKey);

      if (cachedData) {
        return { items: filterByTerm(cachedData, term) };
      }

      const items = await queryClient.fetchQuery({
        queryKey,
        queryFn: async () => {
          const options = await apiClient.fetchCustomFieldOptions(id);
          return toMultiDropdownOptions(options);
        },
      });

      return { items: filterByTerm(items, term) };
    },
    [apiClient, queryClient]
  );

  return (
    <Stack
      spacing={2}
      sx={{ paddingTop: applicationFields.length > 0 ? 1 : 0 }}
    >
      {applicationFields?.map((field, index) => (
        <CustomField
          key={field.reactKey}
          fieldObject={field}
          fetchFields={fetchApplicationFields}
          fetchOptions={fetchCustomFieldOptions}
          onCustomFieldChange={onCustomFieldChange}
          currencies={currencies}
          remove={() => onRemoveField(index)}
          index={index}
          isApplication
        />
      ))}
      <ButtonWrapper>
        <TertiaryButton
          data-provides={'talent-search-add-application-filter-button'}
          onClick={onAddField}
        >
          {t('add_application_filters')}
        </TertiaryButton>
      </ButtonWrapper>
    </Stack>
  );
};

export default Application;
